import React, { useState } from 'react';
import UISectionWrapper, {
  UISectionHeader,
  UISectionBody,
} from '@/components/shared/UISectionWrapper';
import UISectionTitle from '@/components/shared/UISectionTitle';
import UIIconExtraButton from '@/components/shared/UIIconExtraButton';
import './TenantsStatsSection.scss';

export interface StatItem {
  label: string;
  value: string | number;
  color: string;
  percentage?: number;
}

export interface StatsGroupData {
  title: string;
  stats: StatItem[];
  totalLabel?: string;
  totalValue?: string | number;
}

interface TenantsStatsSectionProps {
  agreementStats?: StatsGroupData;
  paymentStats?: StatsGroupData;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

const baseClass = 'TenantsStatsSection';

// Мок-дані для статистики договорів
const mockAgreementStats: StatsGroupData = {
  title: 'Agreement Status',
  totalLabel: 'Tenants',
  totalValue: '127',
  stats: [
    {
      label: 'Signed agreement',
      value: '87',
      color: '#5CB2EF',
      percentage: 68,
    },
    {
      label: 'No signed agreement',
      value: '40',
      color: '#105E96',
      percentage: 32,
    },
  ],
};

// Мок-дані для статистики платежів
const mockPaymentStats: StatsGroupData = {
  title: 'Payment Status',
  stats: [
    { label: 'Paid', value: '64%', color: '#76C102', percentage: 64 },
    { label: 'Debtor', value: '22%', color: '#FB2F54', percentage: 22 },
    { label: 'Not set', value: '14%', color: '#FF9C07', percentage: 14 },
  ],
};

const mockHandlers = {
  onToggleCollapse: () => console.log('Toggle tenants stats collapse'),
};

const TenantsStatsSection: React.FC<TenantsStatsSectionProps> = ({
  agreementStats = mockAgreementStats,
  paymentStats = mockPaymentStats,
  isCollapsed: initialCollapsed = false,
  onToggleCollapse,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(initialCollapsed);

  const handleToggleCollapse = () => {
    setIsCollapsed((prev) => !prev);
    onToggleCollapse?.();
  };

  const headerAction = (
    <UIIconExtraButton
      icon={isCollapsed ? 'expand' : 'collapse'}
      onClick={handleToggleCollapse}
      ariaLabel={isCollapsed ? 'Expand statistics' : 'Collapse statistics'}
    />
  );

  const renderDonutChart = (stats: StatItem[], size: number = 104) => {
    let cumulativePercentage = 0;
    const radius = size / 2;
    const strokeWidth = 12;
    const normalizedRadius = radius - strokeWidth / 2;
    const circumference = normalizedRadius * 2 * Math.PI;

    return (
      <div
        className={`${baseClass}__chart`}
        style={{ width: size, height: size }}
      >
        <svg width={size} height={size} className={`${baseClass}__svg`}>
          {stats.map((stat, index) => {
            const strokeDasharray = `${(stat.percentage! / 100) * circumference} ${circumference}`;
            const strokeDashoffset =
              (-cumulativePercentage * circumference) / 100;
            cumulativePercentage += stat.percentage!;

            return (
              <circle
                key={index}
                cx={radius}
                cy={radius}
                r={normalizedRadius}
                fill="transparent"
                stroke={stat.color}
                strokeWidth={strokeWidth}
                strokeDasharray={strokeDasharray}
                strokeDashoffset={strokeDashoffset}
                className={`${baseClass}__chart-segment`}
                transform={`rotate(-90 ${radius} ${radius})`}
              />
            );
          })}
        </svg>
      </div>
    );
  };

  const renderStatsGroup = (
    statsData: StatsGroupData,
    showChart: boolean = true,
  ) => (
    <div className={`${baseClass}__stat`}>
      <div className={`${baseClass}__data`}>
        <div className={`${baseClass}__field-names`}>
          {statsData.totalLabel && (
            <div
              className={`${baseClass}__category ${baseClass}__category--title`}
            >
              {statsData.totalLabel}
            </div>
          )}
          {statsData.stats.map((stat, index) => (
            <div key={index} className={`${baseClass}__category`}>
              <div
                className={`${baseClass}__dot`}
                style={{ backgroundColor: stat.color }}
              />
              <span>{stat.label}</span>
            </div>
          ))}
        </div>

        <div className={`${baseClass}__field-infos`}>
          {statsData.totalValue && (
            <div
              className={`${baseClass}__field-info ${baseClass}__field-info--title`}
            >
              {statsData.totalValue}
            </div>
          )}
          {statsData.stats.map((stat, index) => (
            <div key={index} className={`${baseClass}__field-info`}>
              {stat.value}
            </div>
          ))}
        </div>
      </div>

      {showChart && (
        <div className={`${baseClass}__graph`}>
          {renderDonutChart(statsData.stats)}
        </div>
      )}
    </div>
  );

  return (
    <UISectionWrapper className={baseClass} align="stretch">
      <UISectionHeader>
        <UISectionTitle
          title="Tenants statistics"
          action={headerAction}
          showDivider={true}
        />
      </UISectionHeader>

      {!isCollapsed && (
        <UISectionBody>
          <div className={`${baseClass}__info`}>
            {renderStatsGroup(agreementStats)}
            {renderStatsGroup(paymentStats)}
          </div>
        </UISectionBody>
      )}
    </UISectionWrapper>
  );
};
export default TenantsStatsSection;
